import React from 'react';
import { TrendingUp, TrendingDown, Minus, Package, DollarSign, Target, Calendar } from 'lucide-react';
import { ProduccionConMetricas } from '../services/produccionService';
import { formatCurrency, formatDate } from '../utils/formatters';
import { FullScreenShell } from './ui/FullScreenShell';

const clasesPorcentajeVendido = (porcentaje: number): string => {
  if (porcentaje >= 80) return 'bg-green-100 text-green-800';
  if (porcentaje >= 50) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

interface ComparativaLotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  producciones: ProduccionConMetricas[];
}

export const ComparativaLotesModal: React.FC<ComparativaLotesModalProps> = ({
  isOpen,
  onClose,
  producciones
}) => {

  // Ordenar por utilidad real descendente
  const produccionesOrdenadas = [...producciones].sort((a, b) => 
    (b.utilidad_real || 0) - (a.utilidad_real || 0)
  );

  // Calcular promedios
  const promedioStock = producciones.reduce((sum, p) => sum + p.stock_inicial, 0) / producciones.length;
  const promedioVendido = producciones.reduce((sum, p) => sum + (p.total_vendido || 0), 0) / producciones.length;
  const promedioUtilidad = producciones.reduce((sum, p) => sum + (p.utilidad_real || 0), 0) / producciones.length;
  const promedioMargen = producciones.reduce((sum, p) => sum + (p.margen_utilidad || 0), 0) / producciones.length;

  const getTrendIcon = (valor: number, promedio: number) => {
    if (valor > promedio * 1.1) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (valor < promedio * 0.9) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getTrendColor = (valor: number, promedio: number) => {
    if (valor > promedio * 1.1) return 'text-green-600 bg-green-50';
    if (valor < promedio * 0.9) return 'text-red-600 bg-red-50';
    return 'text-slate-600 bg-slate-50';
  };

  const header = (
    <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="bg-white/20 p-2 rounded-lg">
          <Package className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">Comparativa de Lotes</h2>
          <p className="text-purple-100 text-sm">
            Análisis comparativo de {producciones.length} lotes
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <FullScreenShell
      isOpen={isOpen}
      onClose={onClose}
      header={header}
      variant="centered"
    >
      {/* Resumen de promedios */}
        <div className="p-6 bg-slate-50 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4">Promedios Generales</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                <Package className="w-4 h-4" />
                Stock Inicial
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {Math.round(promedioStock)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                <DollarSign className="w-4 h-4" />
                Vendido
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {Math.round(promedioVendido)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                <Target className="w-4 h-4" />
                Utilidad
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(promedioUtilidad)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                <TrendingUp className="w-4 h-4" />
                Margen
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {promedioMargen.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        {/* Tabla comparativa */}
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Lote</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Producto</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Stock Inicial</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Vendido</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">% Vendido</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Ingresos</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Gastos</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Utilidad</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Margen</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Fechas</th>
                </tr>
              </thead>
              <tbody>
                {produccionesOrdenadas.map((prod, index) => {
                  const porcentajeVendido = prod.stock_inicial > 0 
                    ? ((prod.total_vendido || 0) / prod.stock_inicial) * 100 
                    : 0;
                  
                  return (
                    <tr 
                      key={prod.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        index === 0 ? 'bg-green-50' : ''
                      }`}
                    >
                      {/* Nombre del lote */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {index === 0 && (
                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">
                              Mejor
                            </span>
                          )}
                          <span className="font-medium text-slate-900">{prod.nombre}</span>
                        </div>
                      </td>

                      {/* Producto */}
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">
                          {prod.producto?.nombre_producto || 'Sin producto'}
                        </span>
                      </td>

                      {/* Stock Inicial */}
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-medium text-slate-900">{prod.stock_inicial}</span>
                          {getTrendIcon(prod.stock_inicial, promedioStock)}
                        </div>
                      </td>

                      {/* Vendido */}
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-medium text-slate-900">{prod.total_vendido || 0}</span>
                          {getTrendIcon(prod.total_vendido || 0, promedioVendido)}
                        </div>
                      </td>

                      {/* % Vendido */}
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${clasesPorcentajeVendido(porcentajeVendido)}`}>
                          {porcentajeVendido.toFixed(0)}%
                        </span>
                      </td>

                      {/* Ingresos */}
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-slate-900">
                          {formatCurrency(prod.total_ingresos || 0)}
                        </span>
                      </td>

                      {/* Costos */}
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-slate-900">
                          {formatCurrency(prod.total_costos || 0)}
                        </span>
                      </td>

                      {/* Utilidad */}
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={`font-bold ${
                            (prod.utilidad_real || 0) > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(prod.utilidad_real || 0)}
                          </span>
                          {getTrendIcon(prod.utilidad_real || 0, promedioUtilidad)}
                        </div>
                      </td>

                      {/* Margen */}
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          getTrendColor(prod.margen_utilidad || 0, promedioMargen)
                        }`}>
                          {(prod.margen_utilidad || 0).toFixed(1)}%
                        </span>
                      </td>

                      {/* Fechas */}
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-slate-600">
                          <Calendar className="w-3 h-3" />
                          {prod.fecha_inicio && prod.fecha_fin ? (
                            <span>
                              {formatDate(prod.fecha_inicio, { day: '2-digit', month: '2-digit' })}
                              {' - '}
                              {formatDate(prod.fecha_fin, { day: '2-digit', month: '2-digit' })}
                            </span>
                          ) : (
                            <span className="text-slate-400">Sin fechas</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      {/* Footer */}
      <div className="p-6 bg-slate-50 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            <p className="font-semibold mb-1">Leyenda:</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span>Por encima del promedio (+10%)</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span>Por debajo del promedio (-10%)</span>
              </div>
              <div className="flex items-center gap-2">
                <Minus className="w-4 h-4 text-slate-400" />
                <span>Dentro del promedio</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>
    </FullScreenShell>
  );
};
